import type {
  ApiErrorBody,
  CalendarResponse,
  SettingsResponse,
  Transaction,
  TransactionInput,
  TransactionsResponse,
} from "../types";

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code = "UNKNOWN_ERROR",
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const apiError = data && typeof data === "object" && "error" in data
      ? (data as ApiErrorBody).error
      : null;
    throw new ApiError(
      apiError?.message ?? "请求失败，请稍后重试。",
      response.status,
      apiError?.code,
    );
  }
  return data as T;
}

export const api = {
  getCalendar: (year: number, month: number) =>
    request<CalendarResponse>(`/api/calendar?year=${year}&month=${month}`),
  getTransactions: (date: string) =>
    request<TransactionsResponse>(`/api/transactions?date=${encodeURIComponent(date)}`),
  createTransaction: (input: TransactionInput) =>
    request<{ transaction: Transaction }>("/api/transactions", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateTransaction: (id: string, input: TransactionInput) =>
    request<{ transaction: Transaction }>(`/api/transactions/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  deleteTransaction: (id: string) =>
    request<{ deleted: true }>(`/api/transactions/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  getSettings: () => request<SettingsResponse>("/api/settings"),
  updateSettings: (dailyExpenseLimitCents: number) =>
    request<SettingsResponse>("/api/settings", {
      method: "PUT",
      body: JSON.stringify({ daily_expense_limit_cents: dailyExpenseLimitCents }),
    }),
};

export { ApiError };
